import { useState, useEffect } from 'react'
import GoogleMapReact from 'google-map-react'
import { Grid, Chip, IconButton, makeStyles, useTheme, useMediaQuery } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import getConfig from 'next/config'
import { useTranslation } from 'next-i18next'

const { publicRuntimeConfig } = getConfig()
const GOOGLE_MAP_API_KEY = publicRuntimeConfig.googleMapApiKey

const useStyles = makeStyles(theme => ({
  branchBox: {
    fontSize: 18,
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    '& h4': {
      fontWeight: 400,
      fontSize: 20,
      color: theme.palette.primary.main,
      margin: `${theme.spacing(1)}px 0`,
      cursor: 'pointer',
    },
    '& h5': {
      fontWeight: 400,
      fontSize: 16,
      color: 'black',
      margin: `${theme.spacing(1)}px 0`,
      color: '#404041',
    },
    '& img': {
      marginTop: theme.spacing(1),
    }
  },
  topic: {
    fontWeight: 400,
    fontSize: 18,
    color: 'black',
    margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
  },
  phone: {
    color: '#828282',
    margin: `${theme.spacing(1)}px 0`,
    fontSize: 16,
  },
  distance: {
    margin: 0,
    color: theme.palette.primary.main,
  },
  searching: {
    color: 'grey',
    textAlign: 'center',
  },
  groupName: {
    fontSize: '14px'
  }
}))

const useEmbedStyles = makeStyles(theme => ({
  branchBox: {
    fontSize: 14,
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    '& h4': {
      fontWeight: 400,
      fontSize: 14,
      color: theme.palette.primary.main,
      margin: `${theme.spacing(1)}px 0`,
      cursor: 'pointer',
    },
    '& h5': {
      fontWeight: 400,
      fontSize: 14,
      color: 'black',
      margin: `${theme.spacing(1)}px 0`,
      color: '#404041',
    },
    '& img': {
      marginTop: theme.spacing(1),
    }
  },
  topic: {
    fontWeight: 400,
    fontSize: 14,
    color: 'black',
    margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
  },
  phone: {
    color: '#828282',
    margin: `${theme.spacing(1)}px 0`,
    fontSize: 14,
  },
  distance: {
    margin: 0,
    color: theme.palette.primary.main,
  },
  searching: {
    color: 'grey',
    textAlign: 'center',
  },
  groupName: {
    fontSize: '14px'
  }
}))

const Marker = ({ data, activeBranch }) => {
  return (
    <img
      src="/static/img/map/shippop-location.svg"
      style={{
        position: 'absolute',
        transform: 'translate(-50%, -100%)',
        width: activeBranch && activeBranch.id === data.id ? 45 : 30,
        height: activeBranch && activeBranch.id === data.id ? 45 : 30,
        cursor: 'pointer',
      }}
    />
  )
}

const UserMarker = () => {
  return (
    <img
      src="/static/img/map/user-location.svg"
      style={{ position: 'absolute', transform: 'translate(-50%, -50%)' }}
    />
  )
}

const DropoffMap = ({
  locationsData = [],
  userLocation = null,
  showUserLocation = false,
  height = 500,
  isLoading = false,
  isEmbed = false,
  displayWorkHours = true
}) => {
  const [centerLocation, setCenterLocation] = useState(null)
  const [activeBranch, setActiveBranch] = useState(null)
  const [isActiveBranchNearest, setActiveBranchNearest] = useState(false)
  
  const classes = isEmbed ? useEmbedStyles() : useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const { t } = useTranslation('dropoff')

  useEffect(() => {
    if (userLocation) {
      setCenterLocation(userLocation)
    }
  }, [userLocation])

  useEffect(() => {
    if (activeBranch) {
      setCenterLocation({ lat: activeBranch.lat, lng: activeBranch.long })
    }
  }, [activeBranch])

  useEffect(() => {
    setActiveBranch(null)
  },[locationsData])

  const renderBranchChoiceBox = (branchData, isNearest) => {
    const { name_station, description, distance = null, phone, id, group } = branchData

    let kilometre = null
    let renderDistance = () => ''

    if (distance !== null) {
      kilometre = parseFloat(distance) * 100
      renderDistance = () => {
        if (kilometre >= 1) {
          return `${kilometre.toFixed(2)} ${t('km')}`
        } else {
          return `${(kilometre * 1000).toFixed(0)} ${t('m')}`
        }
      }
    }

    const pinIconWidth = isEmbed ? 20 : (isMobile ? 25 : 30)

    return (
      <Grid container className={classes.branchBox} key={id}>
        <Grid item container xs={2} direction="column" alignItems="center">
          <img src="/static/img/map/pinned-location.svg" style={{ width: pinIconWidth }} />
        </Grid>
        <Grid item xs={10}>
          <h4
            onClick={() => {
              setActiveBranch(branchData)
              setActiveBranchNearest(isNearest)
            }}
          >
            {name_station}
          </h4>
          {group && <p className={classes.groupName}>{group}</p>}
          <h5>{description}</h5>
          <p className={classes.phone}>{t('tel')} {phone}</p>
          {kilometre !== null ? (
            <Grid container direction="row" alignItems="center">
              <p className={classes.distance}>{renderDistance()}</p>
              {isNearest ? (
                <>
                  <div style={{ width: 20 }} />
                  <Chip
                    size="small"
                    label={<p style={{ fontSize: 16 }}>{t('nearest')}</p>}
                    color="primary"
                  />
                </>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    )
  }

  const renderBranchDetails = () => {
    let kilometre = null
    let renderDistance = () => ''
    if ('distance' in activeBranch) {
      kilometre = parseFloat(activeBranch.distance) * 100
      renderDistance = () => {
        if (kilometre >= 1) {
          return `${kilometre.toFixed(2)} ${t('km')}`
        } else {
          return `${(kilometre * 1000).toFixed(0)} ${t('m')}`
        }
      }
    }

    return (
      <Grid container className={classes.branchBox}>
        <Grid item container xs={2} direction="column" alignItems="center">
          <IconButton
            onClick={() => {
              setActiveBranch(null)
              setActiveBranchNearest(false)
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        </Grid>
        <Grid item xs={10}>
          <h4>{activeBranch['name_station']}</h4>
          {kilometre !== null ? (
            <Grid container direction="row">
              <p className={classes.distance}>{renderDistance()}</p>
              {isActiveBranchNearest ? (
                <>
                  <div style={{ width: 20 }} />
                  <Chip
                    size="small"
                    label={<p style={{ fontSize: 16 }}>{t('nearest')}</p>}
                    color="primary"
                  />
                </>
              ) : null}
            </Grid>
          ) : null}
          <h5 className={classes.topic}>{t('address')}</h5>
          <h5>{activeBranch.description}</h5>
          <h5 className={classes.topic}>{t('tel')}</h5>
          <h5>{activeBranch.phone}</h5>
          {displayWorkHours && <h5 className={classes.topic}>{t('working-hours')}</h5>}
          {displayWorkHours && <h5>{t('weekday')} {activeBranch['weekday_service']}</h5>}
          {displayWorkHours && <h5>{t('sat')} {activeBranch['sat_service']}</h5>}
          {displayWorkHours && <h5>{t('sun')} {activeBranch['sun_service']}</h5>}
        </Grid>
      </Grid>
    )
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Grid container style={{ width: '100%', height: '100%' }}>
        <Grid item xs={12} md={8} style={{ height: height }}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: GOOGLE_MAP_API_KEY, language: 'en' }}
            center={
              centerLocation
                ? centerLocation
                : { lat: 13.7563802, lng: 100.5324896 }
            }
            defaultZoom={16}
            onChildClick={(_, childProps) => {
              setActiveBranch(childProps.data)
            }}
          >
            {locationsData.map((loc) => (
              <Marker lat={loc.lat} lng={loc.long} data={loc} key={loc.id} activeBranch={activeBranch} />
            ))}
            {userLocation && showUserLocation ? (
              <UserMarker lat={userLocation.lat} lng={userLocation.lng} />
            ) : null}
          </GoogleMapReact>
        </Grid>
        <Grid item xs={12} md={4}>
          {locationsData.length === 0 ? (
            isLoading ? (
              <h2 className={classes.searching}>{t('searching')}</h2>
            ) : (
              <h2 className={classes.searching}>{t('services-not-found')}</h2>
            )
          ) : null}
          {locationsData.length > 0 && (
            <>
            {(activeBranch) ? (
              <div style={{ height: height, overflowY: 'scroll' }}>
                {renderBranchDetails()}
              </div>
            ) : (
              <div style={{ height: height, overflowY: 'scroll' }}>
                {locationsData.map((loc, index) => 
                  renderBranchChoiceBox(loc, index === 0)
                )}
              </div>
            )}
            </>
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default DropoffMap
